import { useState, type ReactElement } from 'react';
import { api } from '../shared/api/endpoints';
import { useLocale } from '../shared/i18n/locale';
import { actorLabel, describeAudit } from '../shared/lib/audit-text';
import { useReasonNames } from '../shared/lib/use-reason-names';
import { formatDateTime } from '../shared/lib/format';
import { useAsync } from '../shared/lib/use-async';
import {
  Card,
  ConfirmDialog,
  Drawer,
  Field,
  OrderNumber,
  PlanFactRow,
  StatusChip,
  dangerButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';
import { AssignmentPanel } from './AssignmentPanel';

/**
 * Карточка заказа — drawer справа (разд. 5).
 *
 * Панель, а не модальное окно на весь экран: разбор идёт **в контексте очереди**, и
 * таблица остаётся видимой. У Noor это модалка — для мерчанта нормально, для работы с
 * очередью нет.
 *
 * Порядок блоков задан спецификацией и не произволен: сверху план/факт — то, ради чего
 * карточку открывают в споре о времени, затем участники, состав и журнал.
 */
export function OrderDrawer({
  orderId,
  onClose,
  onChanged,
}: {
  readonly orderId: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): ReactElement {
  const { locale, messages } = useLocale();
  const reasonNames = useReasonNames(locale);
  const order = useAsync(() => api.order(orderId), [orderId]);
  const audit = useAsync(() => api.orderAudit(orderId), [orderId]);
  const zones = useAsync(() => api.zones(), []);

  const [assigning, setAssigning] = useState(false);
  const [confirm, setConfirm] = useState<null | 'cancel' | 'return'>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const data = order.data;

  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const saveCoordinates = async (): Promise<void> => {
    setError(null);

    try {
      await api.setOrderCoordinates(orderId, Number(lat), Number(lng));
      setLat('');
      setLng('');
      onChanged();
      order.reload();
    } catch (cause) {
      setError(String(cause));
    }
  };

  const act = async (): Promise<void> => {
    if (confirm === null) {
      return;
    }

    try {
      if (confirm === 'cancel') {
        await api.cancelOrder(orderId, reason);
      } else {
        await api.returnToCt(orderId, reason);
      }

      setConfirm(null);
      setReason('');
      onChanged();
      order.reload();
    } catch (cause) {
      setError(String(cause));
    }
  };

  return (
    <Drawer
      onClose={onClose}
      title={
        data === null ? (
          <span>{messages.cabinet.common.loading}</span>
        ) : (
          <>
            <OrderNumber externalNumber={data.external_number} displayId={data.display_id ?? ''} />
            <StatusChip
              status={data.status ?? 'new'}
              goodsLocation={data.goods_location}
              atRisk={data.at_risk === true}
            />
          </>
        )
      }
      actions={
        data === null ? null : (
          <>
            <button type="button" style={primaryButtonStyle} onClick={() => setAssigning(true)}>
              {messages.cabinet.assignment.assign}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => setConfirm('return')}>
              {messages.cabinet.resolutions.returnToCt}
            </button>
            <button type="button" style={dangerButtonStyle} onClick={() => setConfirm('cancel')}>
              {messages.cabinet.resolutions.decisionCancel}
            </button>
          </>
        )
      }
    >
      {order.error === null ? null : (
        <div style={{ color: 'var(--danger)' }}>
          {/* Отказ по отделу — не по роли (ADR-0030): права у диспетчера те же. */}
          {order.error.code === 'department_forbidden'
            ? messages.cabinet.common.departmentForbidden
            : order.error.status === FORBIDDEN
              ? messages.cabinet.common.forbidden
              : `${messages.cabinet.common.error}: ${order.error.code}`}
        </div>
      )}

      {data === null ? null : (
        <>
          <Card>
            <h2>{messages.cabinet.order.planFact}</h2>
            <PlanFactRow
              label={messages.cabinet.order.shouldArrive}
              planned={data.promised_at}
              actual={data.eta_delivery_at}
            />
            <Field label={messages.cabinet.order.recipient} value={data.recipient_name ?? ''} />
            <Field
              label={messages.cabinet.create.address}
              value={data.delivery_address?.raw ?? ''}
            />
          </Card>

          {/*
            FR-505: без точки заказ не попадает ни в зону, ни в маршрут. Поле открыто и
            для заказа с точкой — геокодер мог поставить её не на тот дом.
          */}
          <Card>
            <h2>{messages.cabinet.order.coordinates}</h2>
            {data.delivery_address?.point == null ? (
              <span style={{ color: 'var(--warn)' }}>
                {messages.cabinet.order.coordinatesMissing}
              </span>
            ) : (
              <span className="num">
                {`${data.delivery_address.point.lat}, ${data.delivery_address.point.lng}`}
                {data.geocoding_status === 'manual'
                  ? ` · ${messages.cabinet.order.coordinatesManual}`
                  : ''}
              </span>
            )}
            <Field
              label={messages.cabinet.order.zone}
              value={
                (zones.data ?? []).find((zone) => zone.id === data.zone_id)?.name ??
                messages.cabinet.order.noZone
              }
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 'var(--sp-2)',
                alignItems: 'end',
              }}
            >
              <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>
                  {messages.cabinet.order.latitude}
                </span>
                <input
                  inputMode="decimal"
                  style={{ minWidth: 0 }}
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                />
              </label>
              <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>
                  {messages.cabinet.order.longitude}
                </span>
                <input
                  inputMode="decimal"
                  style={{ minWidth: 0 }}
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                />
              </label>
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={lat.trim() === '' || lng.trim() === ''}
                onClick={() => void saveCoordinates()}
              >
                {messages.cabinet.order.saveCoordinates}
              </button>
            </div>
          </Card>

          <Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
              <Field
                label={messages.cabinet.order.mode}
                value={data.mode === undefined ? '' : messages.cabinet.modes[data.mode]}
              />
              <Field label={messages.cabinet.order.driver} value={data.driver?.name ?? ''} />
              <Field
                label={messages.cabinet.order.cargoCategory}
                value={data.cargo_category_id ?? ''}
              />
              <Field
                label={messages.cabinet.order.equipment}
                value={data.equipment_type_id ?? ''}
              />
            </div>
            {/* FR-596: телефон курьера получателю не передаётся — в карточке помечен. */}
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-faint)' }}>
              {messages.cabinet.order.driverPhoneNote}
            </span>
          </Card>

          <Card>
            <h2>{messages.cabinet.order.items}</h2>
            {(data.items ?? []).map((item, index) => (
              <div key={`${item.name}-${index}`} style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <span>{item.name}</span>
                <span className="num">{item.quantity}</span>
              </div>
            ))}
          </Card>

          <Card>
            <h2>{messages.cabinet.order.changeLog}</h2>
            {(audit.data?.data ?? []).map((entry) => {
              const text = describeAudit(entry, messages, locale, reasonNames);

              return (
                <div
                  key={entry.id}
                  style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--sp-3)' }}
                >
                  <span className="num" style={{ color: 'var(--text-muted)' }}>
                    {formatDateTime(entry.created_at)}
                  </span>
                  <span>
                    {text.event}
                    {(entry.repeat ?? 1) > 1 ? ` ×${String(entry.repeat)}` : ''}
                    <span className="sub">
                      {[actorLabel(entry, messages).name, text.details]
                        .filter((part) => part !== '')
                        .join(' · ')}
                    </span>
                  </span>
                </div>
              );
            })}
          </Card>
        </>
      )}

      {assigning ? (
        <AssignmentPanel
          orderId={orderId}
          onClose={() => setAssigning(false)}
          onAssigned={() => {
            setAssigning(false);
            onChanged();
            order.reload();
          }}
        />
      ) : null}

      {confirm === null ? null : (
        <ConfirmDialog
          title={
            confirm === 'cancel'
              ? messages.cabinet.resolutions.decisionCancel
              : messages.cabinet.resolutions.returnToCt
          }
          description={
            confirm === 'cancel'
              ? messages.cabinet.order.title
              : messages.cabinet.resolutions.returnToCt
          }
          requireReason
          reason={reason}
          onReason={setReason}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void act()}
        />
      )}

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}
    </Drawer>
  );
}

const FORBIDDEN = 403;
